import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Sparkles, Loader2, Check } from "lucide-react";

const URGENCY = [
  { id: "normal", label: "Normal", desc: "5-10 días" },
  { id: "urgente", label: "Urgente", desc: "+20% · 2-3 días" },
  { id: "muy_urgente", label: "Muy urgente", desc: "+35% · 24-48h" },
];

const TAX_OPTIONS = [
  { id: "none", label: "Sin impuesto", rate: 0 },
  { id: "iva", label: "IVA", rate: 21 },
  { id: "igic", label: "IGIC", rate: 7 },
];

export default function Generator() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [params] = useSearchParams();
  const [templates, setTemplates] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    work_type: params.get("template") || "reformas",
    client_id: "",
    client_name: "",
    client_phone: "",
    client_email: "",
    client_address: "",
    client_type: "particular",
    description: "",
    meters: "",
    hours: "",
    materials_included: true,
    materials_specified: false,
    materials: "",
    urgency: "normal",
    tax_type: user?.default_tax_type || "iva",
  });

  useEffect(() => {
    api.get("/templates").then((r) => setTemplates(r.data));
    api.get("/clients").then((r) => setClients(r.data));
  }, []);

  // sync default tax once user loads
  useEffect(() => {
    if (user?.default_tax_type) setForm((f) => ({ ...f, tax_type: user.default_tax_type }));
  }, [user?.default_tax_type]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onClient = (e) => {
    const id = e.target.value;
    if (!id) return setForm({ ...form, client_id: "", client_name: "", client_phone: "", client_email: "", client_address: "" });
    const c = clients.find((x) => x.id === id);
    if (c) setForm({ ...form, client_id: c.id, client_name: c.name, client_phone: c.phone, client_email: c.email, client_address: c.address });
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.client_name) return toast.error("Indica el nombre del cliente");
    if (!form.description) return toast.error("Describe el trabajo");
    setLoading(true);
    try {
      const payload = {
        ...form,
        meters: parseFloat(form.meters) || 0,
        hours: parseFloat(form.hours) || 0,
        materials: form.materials_included && form.materials_specified ? form.materials : "",
      };
      const { data } = await api.post("/budgets/generate", payload);
      toast.success("Presupuesto generado · revísalo y envíalo");
      nav(`/budgets/${data.id}/edit`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error generando");
    } finally { setLoading(false); }
  };

  return (
    <div data-testid="generator-page">
      <div className="kbar">Generador con IA</div>
      <h1 className="font-display font-extrabold text-3xl sm:text-5xl tracking-tight mt-2">Crea un presupuesto</h1>
      <p className="text-slate-500 mt-2">La IA genera partidas, precios e impuestos según tu sector. Tarda ~10 segundos.</p>

      <form onSubmit={onSubmit} className="mt-8 grid grid-cols-1 lg:grid-cols-3 gap-px bg-slate-200 border border-slate-200">
        <div className="bg-white p-6 lg:col-span-2 space-y-6">
          <div>
            <label className="kbar block mb-3">Tipo de trabajo</label>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {templates.map((t) => (
                <button
                  type="button" key={t.id}
                  data-testid={`tpl-${t.id}`}
                  onClick={() => setForm({ ...form, work_type: t.id })}
                  className={`h-12 px-3 text-sm font-bold border transition-colors ${form.work_type === t.id ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-200 hover:border-slate-300"}`}
                >{t.name}</button>
              ))}
            </div>
          </div>

          <div>
            <label className="kbar block mb-3">Tipo de cliente</label>
            <div className="grid grid-cols-3 gap-2">
              {[
                { id: "particular", label: "Particular", desc: "Vivienda" },
                { id: "empresa", label: "Empresa", desc: "Oficinas / local" },
                { id: "comunidad", label: "Comunidad", desc: "De vecinos" },
              ].map((c) => (
                <button type="button" key={c.id} data-testid={`client-type-${c.id}`} onClick={() => setForm({ ...form, client_type: c.id })} className={`p-3 text-left border transition-colors ${form.client_type === c.id ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-200 hover:border-slate-300"}`}>
                  <div className="font-bold text-sm">{c.label}</div>
                  <div className={`text-xs ${form.client_type === c.id ? "text-slate-300" : "text-slate-500"}`}>{c.desc}</div>
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="kbar block">Descripción del trabajo</label>
            <textarea
              data-testid="gen-description"
              value={form.description} onChange={set("description")}
              rows={3} required
              placeholder="Pintar piso de 80m², 2 manos color claro, paredes y techos…"
              className="w-full mt-2 p-3 border border-slate-200 focus:border-orange-600 focus:ring-2 focus:ring-orange-100 outline-none resize-none"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="kbar block">Metros</label>
              <input data-testid="gen-meters" type="number" min="0" step="0.1" value={form.meters} onChange={set("meters")} className="w-full h-12 mt-2 px-3 border border-slate-200 focus:border-orange-600 focus:ring-2 focus:ring-orange-100 outline-none" />
            </div>
            <div>
              <label className="kbar block">Horas estimadas</label>
              <input data-testid="gen-hours" type="number" min="0" step="0.5" value={form.hours} onChange={set("hours")} className="w-full h-12 mt-2 px-3 border border-slate-200 focus:border-orange-600 focus:ring-2 focus:ring-orange-100 outline-none" />
            </div>
          </div>

          {/* Materials section */}
          <div>
            <label className="kbar block mb-3">¿Incluye materiales?</label>
            <div className="grid grid-cols-2 gap-2">
              <ToggleBtn
                testid="materials-with"
                active={form.materials_included}
                onClick={() => setForm({ ...form, materials_included: true })}
                title="Con materiales"
                desc="Los aporta el autónomo"
              />
              <ToggleBtn
                testid="materials-without"
                active={!form.materials_included}
                onClick={() => setForm({ ...form, materials_included: false, materials_specified: false, materials: "" })}
                title="Sin materiales"
                desc="Los aporta el cliente"
              />
            </div>

            {form.materials_included && (
              <div className="mt-4 pl-4 border-l-2 border-orange-200">
                <label className="kbar block mb-3">¿Especificar materiales?</label>
                <div className="grid grid-cols-2 gap-2">
                  <ToggleBtn
                    testid="materials-spec-yes"
                    active={form.materials_specified}
                    onClick={() => setForm({ ...form, materials_specified: true })}
                    title="Sí, especificar"
                    desc="Yo indico los materiales"
                  />
                  <ToggleBtn
                    testid="materials-spec-no"
                    active={!form.materials_specified}
                    onClick={() => setForm({ ...form, materials_specified: false, materials: "" })}
                    title="No, decide la IA"
                    desc="La IA elige los típicos"
                  />
                </div>

                {form.materials_specified && (
                  <input
                    data-testid="gen-materials"
                    value={form.materials}
                    onChange={set("materials")}
                    placeholder="Ej: pintura plástica blanca Bruguer, 2 cubos de 15L, masilla, cinta"
                    className="w-full h-12 mt-3 px-3 border border-slate-200 focus:border-orange-600 focus:ring-2 focus:ring-orange-100 outline-none"
                  />
                )}
              </div>
            )}
          </div>

          <div>
            <label className="kbar block mb-3">Urgencia</label>
            <div className="grid grid-cols-3 gap-2">
              {URGENCY.map((u) => (
                <button type="button" key={u.id} data-testid={`urgency-${u.id}`} onClick={() => setForm({ ...form, urgency: u.id })} className={`p-3 text-left border transition-colors ${form.urgency === u.id ? "bg-orange-50 border-orange-600" : "bg-white border-slate-200 hover:border-slate-300"}`}>
                  <div className="font-bold text-sm">{u.label}</div>
                  <div className="text-xs text-slate-500">{u.desc}</div>
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="kbar block mb-3">Tipo de gravamen</label>
            <div className="grid grid-cols-3 gap-2">
              {TAX_OPTIONS.map((t) => (
                <button type="button" key={t.id} data-testid={`tax-${t.id}`} onClick={() => setForm({ ...form, tax_type: t.id })} className={`p-3 text-left border transition-colors ${form.tax_type === t.id ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-200 hover:border-slate-300"}`}>
                  <div className="font-bold text-sm">{t.label}</div>
                  <div className={`text-xs ${form.tax_type === t.id ? "text-slate-300" : "text-slate-500"}`}>{t.rate}%</div>
                </button>
              ))}
            </div>
            {user?.default_tax_type && form.tax_type !== user.default_tax_type && (
              <p className="text-xs text-slate-400 mt-2">Tu predeterminado es <span className="font-bold">{TAX_OPTIONS.find(t => t.id === user.default_tax_type)?.label}</span>.</p>
            )}
          </div>
        </div>

        <div className="bg-white p-6 space-y-4">
          <div className="kbar">Cliente</div>
          {clients.length > 0 && (
            <select data-testid="gen-client-select" value={form.client_id} onChange={onClient} className="w-full h-12 px-3 border border-slate-200 focus:border-orange-600 outline-none">
              <option value="">— Cliente nuevo —</option>
              {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          )}
          <input data-testid="gen-client-name" required value={form.client_name} onChange={set("client_name")} placeholder="Nombre del cliente" className="w-full h-12 px-3 border border-slate-200 focus:border-orange-600 outline-none" />
          <input data-testid="gen-client-phone" value={form.client_phone} onChange={set("client_phone")} placeholder="Teléfono" className="w-full h-12 px-3 border border-slate-200 focus:border-orange-600 outline-none" />
          <input data-testid="gen-client-email" type="email" value={form.client_email} onChange={set("client_email")} placeholder="Email" className="w-full h-12 px-3 border border-slate-200 focus:border-orange-600 outline-none" />
          <input data-testid="gen-client-address" value={form.client_address} onChange={set("client_address")} placeholder="Dirección" className="w-full h-12 px-3 border border-slate-200 focus:border-orange-600 outline-none" />

          <button type="submit" disabled={loading} data-testid="gen-submit" className="btn-brand h-14 w-full inline-flex items-center justify-center gap-2 disabled:opacity-50">
            {loading ? (<><Loader2 className="w-5 h-5 animate-spin" /> Generando…</>) : (<><Sparkles className="w-5 h-5" /> Generar con IA</>)}
          </button>
          {loading && <div className="text-xs text-center text-slate-400 animate-pulse-soft">La IA está calculando precios y partidas reales del mercado…</div>}
        </div>
      </form>
    </div>
  );
}

function ToggleBtn({ active, onClick, title, desc, testid }) {
  return (
    <button
      type="button" onClick={onClick} data-testid={testid}
      className={`p-3 text-left border transition-colors relative ${active ? "bg-orange-50 border-orange-600" : "bg-white border-slate-200 hover:border-slate-300"}`}
    >
      {active && <Check className="w-4 h-4 text-orange-600 absolute top-2 right-2" />}
      <div className="font-bold text-sm">{title}</div>
      <div className="text-xs text-slate-500">{desc}</div>
    </button>
  );
}
